<template>
  <v-card class="pa-0" elevation="0">
    <v-sheet class="d-flex justify-space-between" elevation="0">
      <v-chip-group v-model="activeFilterPanel" column base-color="primary">
        <!-- Relevance Filter -->
        <v-chip
          value="relevance"
          density="compact"
          rounded="lg"
          prepend-icon="mdi-menu-down"
          :variant="
            searchStore.filters.relevanceMin !== null || searchStore.filters.relevanceMax !== null
              ? 'flat'
              : 'outlined'
          "
          :color="
            searchStore.filters.relevanceMin !== null || searchStore.filters.relevanceMax !== null
              ? 'primary'
              : 'primary'
          "
        >
          {{ $t('labels.relevance') }}
        </v-chip>

        <!-- Format Filter -->
        <v-chip
          value="format"
          density="compact"
          rounded="lg"
          prepend-icon="mdi-menu-down"
          :variant="searchStore.filters.formats.length > 0 ? 'flat' : 'outlined'"
          :color="searchStore.filters.formats.length > 0 ? 'primary' : 'primary'"
        >
          {{ $t('labels.format') }}
        </v-chip>

        <!-- License Filter -->
        <v-chip
          value="license"
          density="compact"
          rounded="lg"
          prepend-icon="mdi-menu-down"
          :variant="searchStore.filters.licenses.length > 0 ? 'flat' : 'outlined'"
          :color="searchStore.filters.licenses.length > 0 ? 'primary' : 'primary'"
        >
          {{ $t('labels.license') }}
        </v-chip>

        <!-- Size Filter -->
        <v-chip
          value="size"
          density="compact"
          rounded="lg"
          prepend-icon="mdi-menu-down"
          :variant="searchStore.filters.sizes.length > 0 ? 'flat' : 'outlined'"
          :color="searchStore.filters.sizes.length > 0 ? 'primary' : 'primary'"
        >
          {{ $t('labels.size') }}
        </v-chip>

        <!-- Keywords Filter -->
        <v-chip
          class="d-none"
          value="keywords"
          density="compact"
          rounded="lg"
          prepend-icon="mdi-menu-down"
          :variant="searchStore.filters.keywords.length > 0 ? 'flat' : 'outlined'"
          :color="searchStore.filters.keywords.length > 0 ? 'primary' : 'primary'"
        >
          {{ $t('labels.keywords') }}
        </v-chip>

        <!-- Clear All Filters -->
        <v-chip
          v-if="searchStore.hasActiveFilters"
          :text="$t('strings.clear-filters')"
          prepend-icon="mdi-filter-remove"
          variant="text"
          rounded="xl"
          density="compact"
          class="text-secondary"
          @click.stop="searchStore.clearFilters()"
        ></v-chip>
      </v-chip-group>

      <v-btn
        :prepend-icon="searchStore.savedDatasetsCount > 0 ? 'mdi-bookmark-multiple' : 'mdi-bookmark-multiple-outline'"
        variant="outlined"
        rounded="xl"
        density="comfortable"
        color="secondary"
        @click="searchStore.setCurrentView('saved')"
      >
        {{ $t('labels.saved') }}
        <v-badge
          v-if="searchStore.savedDatasetsCount > 0"
          :content="searchStore.savedDatasetsCount"
          color="secondary"
          inline
          class="ml-2"
        />
      </v-btn>
    </v-sheet>

    <!-- Relevance Filter Panel -->
    <v-card-text v-if="activeFilterPanel === 'relevance'" class="d-flex flex-row align-center">
      <v-chip
        :text="$t('labels.all')"
        :variant="
          searchStore.filters.relevanceMin === null && searchStore.filters.relevanceMax === null
            ? 'flat'
            : 'outlined'
        "
        density="compact"
        color="primary"
        class="flex-shrink-0"
        @click="
          searchStore.setFilter('relevanceMin', null);
          searchStore.setFilter('relevanceMax', null)
        "
      ></v-chip>
      <v-divider class="mx-2 flex-shrink-0" vertical></v-divider>
      <v-row class="align-center" dense>
        <v-col cols="12">
          <v-range-slider
            v-model="relevanceRange"
            :min="isCategorical ? 0 : relevanceNumericConfig.min"
            :max="isCategorical ? relevanceCategoryLevels.length - 1 : relevanceNumericConfig.max"
            :step="isCategorical ? 1 : relevanceNumericConfig.step"
            :ticks="relevanceTickLabels"
            show-ticks="always"
            color="primary"
            thumb-label="always"
            strict
            class="mt-4 mx-4"
          >
            <template v-if="isCategorical" v-slot:thumb-label="{ modelValue }">
              {{ relevanceCategoryLevels[modelValue]?.label }}
            </template>
          </v-range-slider>
        </v-col>
        <v-col cols="12" class="align-center">
          <div class="text-caption text-grey text-center">
            {{ relevanceHint }}
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Format Filter Panel (Multi-select) -->
    <v-card-text v-if="activeFilterPanel === 'format'" class="d-flex flex-row align-center">
      <v-chip
        :text="$t('labels.all')"
        :variant="searchStore.filters.formats.length === 0 ? 'flat' : 'outlined'"
        density="compact"
        color="primary"
        class="flex-shrink-0"
        @click="searchStore.setFilter('formats', [])"
      ></v-chip>
      <v-divider class="mx-2 flex-shrink-0" vertical></v-divider>
      <v-chip-group
        :model-value="searchStore.filters.formats"
        column
        multiple
        base-color="primary"
        @update:model-value="searchStore.setFilter('formats', $event)"
      >
        <v-chip
          v-for="format in searchStore.formatsFromResults"
          :key="format"
          :text="format"
          :value="format"
          variant="outlined"
          filter
          density="compact"
        ></v-chip>
      </v-chip-group>
    </v-card-text>

    <!-- License Filter Panel (Multi-select) -->
    <v-card-text v-if="activeFilterPanel === 'license'" class="d-flex flex-row align-center">
      <v-chip
        :text="$t('labels.all')"
        :variant="searchStore.filters.licenses.length === 0 ? 'flat' : 'outlined'"
        density="compact"
        color="primary"
        class="flex-shrink-0"
        @click="searchStore.setFilter('licenses', [])"
      ></v-chip>
      <v-divider class="mx-2 flex-shrink-0" vertical></v-divider>
      <v-chip-group
        :model-value="searchStore.filters.licenses"
        column
        multiple
        base-color="primary"
        @update:model-value="searchStore.setFilter('licenses', $event)"
      >
        <v-chip
          v-for="license in searchStore.licensesFromResults"
          :key="license"
          :text="license"
          :value="license"
          variant="outlined"
          filter
          density="compact"
        ></v-chip>
      </v-chip-group>
    </v-card-text>

    <!-- Size Filter Panel (Multi-select) -->
    <v-card-text v-if="activeFilterPanel === 'size'" class="d-flex flex-row align-center">
      <v-chip
        :text="$t('labels.all')"
        :variant="searchStore.filters.sizes.length === 0 ? 'flat' : 'outlined'"
        density="compact"
        color="primary"
        class="flex-shrink-0"
        @click="searchStore.setFilter('sizes', [])"
      ></v-chip>
      <v-divider class="mx-2 flex-shrink-0" vertical></v-divider>
      <v-chip-group
        :model-value="searchStore.filters.sizes"
        column
        multiple
        base-color="primary"
        @update:model-value="searchStore.setFilter('sizes', $event)"
      >
        <v-chip
          text="Byte (< 1 KB)"
          value="byte"
          variant="outlined"
          filter
          density="compact"
        ></v-chip>
        <v-chip
          text="KByte (1 KB - 1 MB)"
          value="kb"
          variant="outlined"
          filter
          density="compact"
        ></v-chip>
        <v-chip
          text="MByte (1 MB - 1 GB)"
          value="mb"
          variant="outlined"
          filter
          density="compact"
        ></v-chip>
        <v-chip
          text="GByte (≥ 1 GB)"
          value="gb"
          variant="outlined"
          filter
          density="compact"
        ></v-chip>
      </v-chip-group>
    </v-card-text>

    <!-- Keywords Filter Panel -->
    <v-card-text v-if="activeFilterPanel === 'keywords'" class="d-flex flex-column">
      <v-combobox
        v-model="searchStore.filters.keywords"
        :label="$t('strings.add-keywords')"
        chips
        clearable
        multiple
        variant="outlined"
        density="compact"
        prepend-inner-icon="mdi-tag-multiple"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip v-bind="props" :text="item.raw" closable density="compact"></v-chip>
        </template>
      </v-combobox>
    </v-card-text>

    <v-divider></v-divider>
  </v-card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSearchStore } from '@/stores/search'
import {
  relevanceConfig,
  isCategoricalRelevance,
  getCategoricalIndex,
  getCategoricalValueByIndex,
  getCategoricalLevels,
} from '@/services/relevance'

const searchStore = useSearchStore()
const activeFilterPanel = ref(null)
const { t } = useI18n()

const isCategorical = isCategoricalRelevance()
const relevanceNumericConfig = relevanceConfig.numeric
const relevanceCategoryLevels = computed(() => {
  return getCategoricalLevels().map((level) => ({
    ...level,
    label: t(level.labelKey),
  }))
})

const relevanceHint = computed(() => {
  if (!isCategorical) {
    return t('strings.relevance-hint')
  }

  const localizedLevels = relevanceCategoryLevels.value.map((level) => level.label).join(' • ')
  return `${t('strings.relevance-hint-categorical')}: ${localizedLevels}`
})

// Computed property for range slider binding
const relevanceRange = computed({
  get() {
    if (isCategorical) {
      const minIndex = getCategoricalIndex(searchStore.filters.relevanceMin)
      const maxIndex = getCategoricalIndex(searchStore.filters.relevanceMax)
      return [
        minIndex >= 0 ? minIndex : 0,
        maxIndex >= 0 ? maxIndex : relevanceCategoryLevels.value.length - 1
      ]
    }
    return [
      searchStore.filters.relevanceMin ?? relevanceNumericConfig.min,
      searchStore.filters.relevanceMax ?? relevanceNumericConfig.max
    ]
  },
  set(value) {
    if (isCategorical) {
      const minValue = getCategoricalValueByIndex(value[0])
      const maxValue = getCategoricalValueByIndex(value[1])
      searchStore.setFilter('relevanceMin', value[0] === 0 ? null : minValue)
      searchStore.setFilter('relevanceMax', value[1] === relevanceCategoryLevels.value.length - 1 ? null : maxValue)
    } else {
      searchStore.setFilter('relevanceMin', value[0] === relevanceNumericConfig.min ? null : value[0])
      searchStore.setFilter('relevanceMax', value[1] === relevanceNumericConfig.max ? null : value[1])
    }
  }
})

// Computed for tick labels on the range slider
const relevanceTickLabels = computed(() => {
  if (isCategorical) {
    return relevanceCategoryLevels.value.reduce((acc, cat, index) => {
      acc[index] = cat.label
      return acc
    }, {})
  }

  const ticks = {}
  const min = relevanceNumericConfig.min
  const max = relevanceNumericConfig.max
  const step = relevanceNumericConfig.step

  for (let value = min; value <= max + step / 2; value += step) {
    const normalizedValue = Number(value.toFixed(10))
    ticks[normalizedValue] = String(normalizedValue)
  }

  return ticks
})
</script>
